import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { taskStatusNameColor } from "../../../../../common/vue-helpers/modifiers/task-status-color";
import { ApplicationEnum } from "../../../../../enums/ApplicationEnum";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";
import { ITaskPatientChart, TaskService } from "../../../../../services/task.service";
import config from "../../../../../common/config";
import { NavigationService } from "../../../../../services/navigation.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { AssetsService } from "../../../../../services/assets-service";
import { DownloadService } from "../../../../../services/download.service";
import { PrintService } from "../../../../../services/print.service";
import { IEmployeeList } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import VisitDetailsHosPal from "../../../../CentralCalendar/PatientCalendar/components/VisitDetailsHosPal/VisitDetailsHosPal.vue";
import EmptyTextHandler from "../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import TablePrompt from "../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    components: {
        EmptyTextHandler,
        TablePrompt,
        TypeaheadInput,
        FormSection,
        VisitDetailsHosPal
    },
    props: {
        task: {
            default: null
        },
        columns: {
            type: Number,
            default: 7
        },
        getPatientTasks: {
            type: Function
        }
    },

    data() {
        return {
            _analyticsService: null as AnalyticsService,
            _printService: null as PrintService,
            _downloadService: null as DownloadService,
            _assetService: null as AssetsService,
            _toastrService: null as ToastrService,
            _taskService: null as TaskService,
            _navigationService: null as NavigationService,
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            downloadInProgress: false as boolean,
            printingInProgress: false as boolean,
            reassignInProgress: false as boolean,
            employeeList: [] as IEmployeeList[],
            scheduleEmployee: {} as ITypeaheadOptions,
            isSaving: false as boolean,
            viewingTaskDetails: false as boolean,
            isSavingDetails: false as boolean,
            loadingUsers: false as boolean,
            taskStatusNameColor,
            formatDate
        };
    },
    created() {
        this._navigationService = Container.instance.get(NavigationService);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._assetService = Container.instance.get(AssetsService);
        this._downloadService = Container.instance.get(DownloadService);
        this._printService = Container.instance.get(PrintService);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },

    computed: {
        cantDeleteOrReassignTask() {
            const completedTaskStatuses = [
                "Submitted Pending Co-Signature",
                "Submitted with Signature",
                "Submitted With Signature",
                "Export Ready",
                "To Be Sent",
                "Sent to Physician",
                "Returned with Physician Signature",
                "Completed",
                "Not Exported"
            ];
            return completedTaskStatuses.includes(this.task.Status);
        }
    },

    methods: {
        handleEmployeeNameClick(task: ITaskPatientChart) {
            let redirectResponse;
            let analyticsCategory = "Central-Calendar-Navigation";
            let action = "";
            if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(task.AgencyId)}/employees/employee-center/${
                        task.UserId
                    }?accountId=${task.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
            } else {
                redirectResponse = {
                    redirectUrl: `${config.palliativeUrl}/employees/employee-center/${task.UserId}?accountId=${task.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessPalliative";
            }
            this._navigationService.redirect(redirectResponse, {
                category: analyticsCategory,
                action
            });
        },
        requestDeleteTask() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Delete"
            });
            this.isDeleteRequested = true;
        },
        getEmployeeName(task: ITaskPatientChart) {
            return task.UserFirstName ? `${task.UserFirstName} ${task.UserLastName}` : "";
        },
        handleTaskClick(task: ITaskPatientChart) {
            if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                window.open(`${getHospiceRedirectionUrl(task.AgencyId)}/note/${task.Id}?accountId=${task.AgencyId}`);
            } else {
                window.open(`${config.palliativeUrl}/note/${task.Id}`);
            }
        },
        async deleteTask() {
            try {
                this.isDeleteInProgress = true;
                const payload = {
                    AgencyId: this.task.AgencyId,
                    Application: this.task.Application,
                    PatientId: this.task.PatientId,
                    EventId: [this.task.Id],
                    IsPatient: true
                };
                const response = await this._taskService.patientBulkDelete(payload);
                if (response.isSuccessful) {
                    this._toastrService.success({
                        title: `Task Deleted`,
                        message: `Task has been successfully deleted.`
                    });
                }
                this.getPatientTasks();
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Failed Task Deletion`,
                    message: `Failed to delete Task`
                });
            } finally {
                this.isDeleteInProgress = false;
                this.isDeleteRequested = false;
            }
        },
        cancelDeleteTask() {
            this.isDeleteRequested = false;
        },
        async downloadVisit() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Download"
            });
            try {
                this.downloadInProgress = true;
                const payload = {
                    agencyId: this.task.AgencyId,
                    patientId: this.task.PatientId,
                    application: this.task.Application
                };
                const response = await this._assetService.downloadPatientVisit(this.task.Id, payload);
                this._downloadService.downloadFile(response);
                this._toastrService.success({
                    title: `Task Details Downloaded`,
                    message: `Task has been successfully downloaded.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Failed To Download Task `,
                    message: `Failed To Download Task. Try Again !!`
                });
            } finally {
                this.downloadInProgress = false;
            }
        },
        async printVisit() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Print"
            });
            try {
                this.printingInProgress = true;
                const payload = {
                    agencyId: this.task.AgencyId,
                    patientId: this.task.PatientId,
                    application: this.task.Application
                };
                const response = await this._assetService.downloadPatientVisit(this.task.Id, payload);
                this._printService.print(response);
                this._toastrService.success({
                    title: `Task Details Printed Successfully !!`,
                    message: `Task has been successfully printed.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Failed to print task details !!`,
                    message: `Failed to print. Try Again !!`
                });
            } finally {
                this.printingInProgress = false;
            }
        },
        async handleReassignVisit() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Reassign"
            });
            this.reassignInProgress = true;
            const queryParams = {
                customTaskId: this.task.CustomTaskId,
                application: this.task.Application,
                accountId: this.task.AgencyId,
                patientId: this.task.PatientId
            };
            this.loadingUsers = true;
            let employees = await this._taskService.employeeList(queryParams);
            this.employeeList = employees.filter((employee: any) => {
                return employee.id != this.task.UserId;
            });
            this.loadingUsers = false;
        },
        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.employeeList.filter((employee: any) => {
                    const fullName = `${employee.firstName} ${employee.lastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item: any) => {
                    return {
                        name: `${item.firstName} ${item.lastName}`,
                        value: item.id
                    };
                });
            } else {
                return this.employeeList?.map((item: any) => ({
                    value: item.id,
                    name: `${item.firstName} ${item.lastName}`
                }));
            }
        },
        changescheduleEmployee(data: any) {
            this.scheduleEmployee = data;
        },
        async reassignVisit() {
            try {
                this.isSaving = true;
                const payload = {
                    AccountId: this.task.AgencyId,
                    UserId: this.scheduleEmployee.value,
                    Application: this.task.Application,
                    EpisodeId: this.task.EpisodeId,
                    PatientId: this.task.PatientId,
                    TaskId: this.task.Id
                };
                const response = await this._taskService.reassignVisit(payload);
                if (response && !response?.isSuccessful) {
                    if (response.errorDetails.taskIssues.length) {
                        response.errorDetails.taskIssues.forEach((taskIssue: any) => {
                            this._toastrService.error({
                                title: `Visit Reassigning Failed`,
                                message: taskIssue.message
                            });
                        });
                    } else {
                        this._toastrService.error({
                            title: `Visit Reassigning Failed`,
                            message: `Reassigning Visit Operation Failed, Please Contact Axxess if issue still persists.`
                        });
                    }
                } else {
                    this._toastrService.success({
                        title: `Visit Reassigned`,
                        message: `Visit has been successfully reassigned.`
                    });
                    this.getPatientTasks();
                }
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Visit Reassigning Failed`,
                    message: `Reassigning Visit Operation Failed, Please Contact Axxess if issue still persists.`
                });
            } finally {
                this.isSaving = false;
                this.reassignInProgress = false;
            }
        },
        handleCancelReassign() {
            this.reassignInProgress = false;
            this.scheduleEmployee = {} as ITypeaheadOptions;
        },
        handleViewTaskDetails() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.PatientCharts,
                action: "Task Details"
            });
            this.viewingTaskDetails = true;
        },
        cancelViewTaskDetails() {
            this.viewingTaskDetails = false;
            this.isSaving = false;
        },
        savedTaskDetails() {
            this.viewingTaskDetails = false;
            this.isSaving = false;
            this.getPatientTasks();
        }
    }
});
